import { useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AiChatMessage, { AiChatMessageData } from './AiChatMessage';
import { AI_CHAT_ACCENT_DARK, AI_CHAT_GRADIENT } from './theme';

interface CompanyOption {
  id: number;
  name: string;
}

interface AiChatPanelProps {
  open: boolean;
  onClose: () => void;
  messages: AiChatMessageData[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  isSending: boolean;
  companies: CompanyOption[];
  selectedCompanyId: number | null;
  onSelectCompany: (id: number) => void;
  showCompanyPicker: boolean;
}

export default function AiChatPanel({
  open,
  onClose,
  messages,
  inputValue,
  onInputChange,
  onSend,
  isSending,
  companies,
  selectedCompanyId,
  onSelectCompany,
  showCompanyPicker,
}: AiChatPanelProps) {
  const listEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const canSend = inputValue.trim().length > 0 && !isSending && selectedCompanyId != null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 96,
          right: 24,
          width: { xs: 'calc(100vw - 32px)', sm: 400 },
          height: 560,
          maxHeight: 'calc(100vh - 140px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          zIndex: (theme) => theme.zIndex.modal + 1,
          background: AI_CHAT_GRADIENT,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: AI_CHAT_ACCENT_DARK,
            color: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            🛡️ Guardian Intelligence
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {showCompanyPicker && (
          <Box sx={{ px: 2, py: 1, bgcolor: 'background.default' }}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={selectedCompanyId ?? ''}
              onChange={(e) => onSelectCompany(Number(e.target.value))}
            >
              <MenuItem value="" disabled>
                Elige una empresa
              </MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
          {messages.length === 0 && (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', mt: 4, color: 'rgba(255,255,255,0.85)' }}
            >
              Pregunta lo que quieras saber sobre tus datos: stock, movimientos, tendencias. Te
              respondo con una tabla o un gráfico cuando corresponda.
            </Typography>
          )}
          {messages.map((message) => (
            <AiChatMessage key={message.id} message={message} />
          ))}
          <div ref={listEndRef} />
        </Box>

        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1, bgcolor: '#fff' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={
              selectedCompanyId == null ? 'Elige una empresa para empezar' : 'Escribe tu pregunta...'
            }
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={selectedCompanyId == null || isSending}
          />
          <IconButton
            onClick={onSend}
            disabled={!canSend}
            aria-label="Enviar pregunta"
            sx={{ color: AI_CHAT_ACCENT_DARK }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
}
